if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
      }

      onSubmitHandler(evt) {
        evt.preventDefault();

        // Awarahun: once added, the button reads "Go to bag" and opens the bag
        if (this.awAdded) {
          (this.awPending || Promise.resolve()).then(() => {
            window.AwAdded?.hide();
            if (this.cart?.open) this.cart.open(this.submitButton);
            else window.location = window.routes.cart_url;
          });
          return;
        }

        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);

        // Awarahun: answer the tap straight away (card, "Go to bag", bag count)
        // and let the request finish in the background. Rolled back on error.
        const awFast = Boolean(window.AwAdded && this.cart && !this.closest('quick-add-modal'));
        if (awFast) {
          const quantity = parseInt(new FormData(this.form).get('quantity')) || 1;
          window.AwAdded.show(this.awItem(this.variantIdInput.value));
          window.AwAdded.bump(quantity);
          this.awQuantity = quantity;
          this.setAddedState(true);
        } else {
          this.submitButton.classList.add('loading');
          this.querySelector('.loading__spinner').classList.remove('hidden');
        }

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        const variantId = formData.get('id');
        const quantity = parseInt(formData.get('quantity')) || 1;
        const linesUpdateDeferred = this.createCartLinesUpdateEvent(variantId, quantity);

        this.awPending = fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status && awFast) {
              window.AwAdded.fail(response.description || response.message);
              window.AwAdded.bump(-this.awQuantity);
              this.setAddedState(false);
            }
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: variantId,
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);
              this.dispatchCartErrorEvent(response.description || response.message, 'INVALID');
              linesUpdateDeferred?.reject(new Error(response.description || response.message));

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (!this.cart) {
              this.resolveCartLinesUpdate(linesUpdateDeferred);
              window.location = window.routes.cart_url;
              return;
            }

            this.resolveCartLinesUpdate(linesUpdateDeferred);

            const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: variantId,
                cartData: response,
              }).then(() => {
                CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
              });
            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    CartPerformance.measure("add:paint-updated-sections", () => {
                      this.cart.renderContents(response);
                    });
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              // Awarahun: refresh the bag in the background instead of sliding it open
              if (awFast)
                window.AwAdded.update({
                  image: response.image ? `${response.image}${response.image.includes('?') ? '&' : '?'}width=160` : '',
                });
              CartPerformance.measure("add:paint-updated-sections", () => {
                this.cart.renderContents(response, { open: !awFast });
              });
            }
          })
          .catch((e) => {
            console.error(e);
            if (awFast) {
              window.AwAdded.fail('Check your connection and try again.');
              window.AwAdded.bump(-this.awQuantity);
              this.setAddedState(false);
            }
            this.dispatchCartErrorEvent(e.message || 'Network error', 'SERVICE_UNAVAILABLE');
            linesUpdateDeferred?.reject(e);
          })
          .finally(() => {
            this.awPending = null;
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading__spinner').classList.add('hidden');

            CartPerformance.measureFromEvent("add:user-action", evt);
          });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      // Awarahun: what the "Added to your bag" card shows before the server answers.
      // Read from the JSON that sections/aw-main-product.liquid prints next to the form.
      awItem(variantId) {
        const script = this.closest('[data-aw-item-scope]')?.querySelector('script[data-aw-item]');
        let info = {};
        try {
          info = JSON.parse(script.textContent);
        } catch (e) {}
        const variant = (info.variants && info.variants[variantId]) || {};
        return { title: info.title || '', meta: variant.title || '', image: variant.image || info.image || '' };
      }

      // Awarahun: "Add to bag" ⇄ "Go to bag"
      setAddedState(added) {
        this.awAdded = added;
        this.submitButton.classList.toggle('aw-is-added', added);
        if (added) {
          const label = (window.variantStrings && window.variantStrings.goToBag) || 'Go to bag';
          this.submitButtonText.innerHTML =
            '<span class="aw-btn-label"><svg class="aw-btn-tick" viewBox="0 0 16 16" aria-hidden="true"><path d="M2.5 8.6l3.4 3.3 7.6-7.8"/></svg></span>';
          this.submitButtonText.firstChild.append(label);
        } else {
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
        document.dispatchEvent(new CustomEvent('aw:added-state', { detail: { form: this, added } }));
      }

      toggleSubmitButton(disable = true, text) {
        if (this.awAdded) this.setAddedState(false);
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      createCartLinesUpdateEvent(variantId, quantity) {
        const { CartLinesUpdateEvent } = window.StandardEvents || {};
        if (!CartLinesUpdateEvent) return null;

        const deferred = CartLinesUpdateEvent.createPromise();
        this.dispatchEvent(
          new CartLinesUpdateEvent({
            action: 'add',
            context: 'product',
            lines: [{ merchandiseId: variantId, quantity }],
            promise: deferred.promise,
          })
        );
        return deferred;
      }

      resolveCartLinesUpdate(deferred) {
        if (!deferred) return;
        const { CartLinesUpdateEvent } = window.StandardEvents || {};
        if (!CartLinesUpdateEvent) return;

        const pendingCartDataPromise = typeof CartItems !== 'undefined'
          ? CartItems.fetchCartData()
          : fetch(`${routes.cart_url}.json`).then((response) => response.json());

        pendingCartDataPromise
          .then((cart) => {
            if (!cart?.currency) return deferred.reject(new Error('Missing currency in cart response'));
            deferred.resolve({ cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart) });
          })
          .catch((e) => deferred.reject(e));
      }

      dispatchCartErrorEvent(message, code) {
        const { CartErrorEvent } = window.StandardEvents || {};
        if (!CartErrorEvent) return;
        this.dispatchEvent(new CartErrorEvent({ error: message, code }));
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}
